import React, { useCallback, useEffect } from 'react'
import { useRouter } from 'next/router'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import { apiPaths } from 'api/kinerja/pengawas-sekolah/config'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useAPISubmitPerencanaan } from 'api/kinerja/pengawas-sekolah/perencanaan'
import { Card, CardBody } from 'app/KinerjaShared/components/Card'
import { Header } from 'app/KinerjaShared/components/Header'
import { CustomTabSteps } from 'app/KinerjaPSPegawaiPage/components/CustomTabSteps'
import { DialogPraktikKinerjaConfirmation } from 'app/KinerjaPSPegawaiPage/components/DialogPraktikKinerjaConfirmation'
import { DialogExitPrevention } from 'app/KinerjaPSPegawaiPage/components/DialogExitPrevention'
import ScrollDown from 'app/KinerjaPSPegawaiPage/components/ScrollDown'
import { PRAKTIK_KINERJA_HINTS } from 'app/KinerjaPSPegawaiPage/constants/praktikKinerja'
import { usePraktikKinerja } from 'app/KinerjaPSPegawaiPage/hooks/usePraktikKinerja'
import { usePerencanaanPS } from 'app/KinerjaPSPegawaiPage/hooks/usePerencanaan'
import useWarn from 'app/KinerjaShared/hooks/useWarn'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useStoreByUserId } from 'app/KinerjaPSPegawaiPage/hooks/useStoreByUserId'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'
// V2 Components import
import { useSatuanPendidikanV2 } from 'app/KinerjaPSPegawaiPage/hooks/useSatuanPendidikanV2'
import TabV2PraktikKinerja from './TabContent/PraktikKinerja'
import TabV2PilihSatuanPendidikan from './TabContent/PilihSatuanPendidikan'
import TabV2CekRangkuman from './TabContent/CekRangkuman'
import { FooterActionV2 } from 'app/KinerjaPSPegawaiPage/components/FooterActionV2'

import type {
  TPraktikKinerjaFormV2,
  TPerencanaanPraktikKinerjaV2RequestBody,
} from 'types/kinerja-ps-pegawai'

const TENTANG_PRAKTIK_KINERJA_URL =
  process.env.NEXT_PUBLIC_PUSAT_INFORMASI_BASE_URL

const PraktikKinerja = () => {
  const router = useRouter()
  const toast = useToast()

  const { trackEvent } = usePPSTrackEvent()

  // set Store by User ID
  useStoreByUserId()

  const [selectedIndex, setSelectedIndex] = React.useState<number>(0)
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] =
    React.useState<boolean>(false)
  const [isExitPreventionDialogOpen, setExitPreventionDialogOpen] =
    React.useState<boolean>(false)

  const { period } = usePeriodContext()

  // Left Sidebar Hints
  const activeHints = PRAKTIK_KINERJA_HINTS[selectedIndex]

  // Form Data
  const formMethods = useForm<TPraktikKinerjaFormV2>({
    mode: 'onChange',
    defaultValues: {
      selectedPraktikKinerjaIds: [],
      selectedSatuanPendidikanIds: [],
    },
  })
  const {
    formState: { isDirty },
    handleSubmit,
    setValue,
  } = formMethods

  const { isPraktikKinerjaEditable, apiStatus, perencanaan } =
    usePerencanaanPS()

  const handleSuccessPraktikKinerja = useCallback(
    (data) => {
      /** Auto select all RHK */
      if (isPraktikKinerjaEditable)
        setValue(
          'selectedPraktikKinerjaIds',
          data.map(({ id }) => id)
        )
    },
    [isPraktikKinerjaEditable, setValue]
  )
  const { praktikKinerjaData } = usePraktikKinerja(handleSuccessPraktikKinerja)

  const handleSuccessSatuanPendidikan = useCallback(
    (data) => {
      /** Auto select all satuan pendidikan */
      if (isPraktikKinerjaEditable)
        setValue(
          'selectedSatuanPendidikanIds',
          data.map(({ npsn }) => npsn)
        )
    },
    [isPraktikKinerjaEditable, setValue]
  )

  const { satuanPendidikanData } = useSatuanPendidikanV2(
    praktikKinerjaData.map(({ id }) => id),
    handleSuccessSatuanPendidikan
  )

  /** Handle set value from perencanaan API */
  useEffect(() => {
    if (isPraktikKinerjaEditable) return

    const rhkPSValues = perencanaan?.praktikKinerjaRhkPsV2 || []
    const satuanPendidikanValues =
      perencanaan?.praktikKinerjaSatuanPendidikanV2 || []

    setValue(
      'selectedPraktikKinerjaIds',
      rhkPSValues.map(({ id }) => id)
    )
    setValue(
      'selectedSatuanPendidikanIds',
      satuanPendidikanValues.map(({ npsn }) => npsn)
    )
  }, [isPraktikKinerjaEditable, perencanaan, setValue])

  // API Submit Praktik Kinerja form
  const { isLoading: isSubmitting, mutateAsync } =
    useAPISubmitPerencanaan<TPerencanaanPraktikKinerjaV2RequestBody>(
      apiPaths.praktikKinerjaV2
    )

  const handleBackStepAction = () => {
    if (selectedIndex === 0) {
      return
    }

    setSelectedIndex(selectedIndex - 1)
  }

  const handleNextStepAction = () => {
    if (selectedIndex === 2) {
      setConfirmationDialogOpen(true)
      return
    }

    setSelectedIndex(selectedIndex + 1)
  }

  const submit = handleSubmit(async (formData) => {
    const { selectedPraktikKinerjaIds, selectedSatuanPendidikanIds } = formData

    try {
      await mutateAsync({
        periodeId: period.id,
        praktikKinerjaRhkPsId: selectedPraktikKinerjaIds,
        praktikKinerjaNpsn: selectedSatuanPendidikanIds,
      })

      // Track submission
      trackEvent('perencanaan pegawai', 'save draft perencanaan', {
        aspect: 'praktik kinerja',
      })

      setConfirmationDialogOpen(false)
      resetPreventRouteChange()

      // Display toast
      toast({
        message: 'Berhasil simpan Praktik Kinerja!',
        type: 'success',
      })

      // Reditect to Beranda
      setTimeout(() => {
        router.push(
          `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/perencanaan/beranda`
        )
      }, 500)
    } catch (e) {
      setConfirmationDialogOpen(false)
      toast({
        message: 'Gagal simpan Praktik Kinerja. Coba lagi.',
      })
    }
  })

  const steps = [
    {
      title: '1. Praktik Kinerja',
      content: <TabV2PraktikKinerja praktikKinerjaData={praktikKinerjaData} />,
    },
    {
      title: '2. Pilih Satuan Pendidikan',
      content: (
        <TabV2PilihSatuanPendidikan
          praktikKinerjaData={praktikKinerjaData}
          satuanPendidikanData={satuanPendidikanData}
        />
      ),
    },
    {
      title: '3. Rangkuman',
      content: (
        <TabV2CekRangkuman
          praktikKinerjaData={praktikKinerjaData}
          satuanPendidikanData={satuanPendidikanData}
        />
      ),
    },
  ]

  const { reset: resetPreventRouteChange } = useWarn({
    enabled: isDirty,
    bindPopState: true,
    onWarn: () => {
      setExitPreventionDialogOpen(true)
    },
  })

  return (
    <div className="pb-20">
      <Header title="Praktik Kinerja" className="mb-9 print:hidden" />

      {apiStatus === 'loading' && (
        <div className="flex justify-center">
          <Spinner />
        </div>
      )}

      {apiStatus === 'success' && (
        <>
          <div className="grid grid-cols-10 gap-x-3">
            <div className="col-span-3">
              <Card className="sticky bg-white border rounded-md border-subdued top-24">
                <CardBody className="p-6 space-y-6">
                  <Text variant="heading-sm" className="flex-1">
                    {activeHints.title}
                  </Text>
                  <div className="space-y-4 text-left">
                    {activeHints.hints.map((hint, index) => (
                      <div key={index} className="flex space-x-3">
                        {activeHints.type === 'bullet' ? (
                          <Icon>check_circle_outlined</Icon>
                        ) : (
                          <Text>{index + 1}. </Text>
                        )}
                        <div className="space-y-1">
                          <Text>{hint}</Text>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    color="white"
                    onClick={() => {
                      window.open(TENTANG_PRAKTIK_KINERJA_URL, '_blank')
                    }}
                    size="md"
                    variant="ghost"
                    fullWidth
                    className="!text-blue-70 !outline-none space-x-1"
                  >
                    <Icon color="informational">help</Icon>
                    <span>Tentang Praktik Kinerja</span>
                  </Button>
                </CardBody>
              </Card>
            </div>

            <FormProvider {...formMethods}>
              <div className="col-span-7">
                <CustomTabSteps
                  selectedIndex={selectedIndex}
                  onChange={(index) => setSelectedIndex(index)}
                  steps={steps}
                  panelClassName="!px-6"
                />
              </div>
            </FormProvider>
          </div>

          <DialogPraktikKinerjaConfirmation
            isOpen={isConfirmationDialogOpen}
            onConfirm={submit}
            onClose={() => {
              setConfirmationDialogOpen(false)
            }}
            btnFullWidth
          />

          <DialogExitPrevention
            isOpen={isExitPreventionDialogOpen}
            onConfirm={() => {}}
            onClose={() => {
              setExitPreventionDialogOpen(false)
            }}
          />

          {isPraktikKinerjaEditable && (
            <FooterActionV2
              hasBackButton
              isBackDisabled={selectedIndex === 0}
              isNextDisabled={isSubmitting}
              nextLabel={
                <div className="flex items-center space-x-1">
                  <span>Selanjutnya</span>
                </div>
              }
              onBack={handleBackStepAction}
              onNext={handleNextStepAction}
            />
          )}

          <ScrollDown />
        </>
      )}
    </div>
  )
}
export default PraktikKinerja
